/**
 * @fileoverview
 * Axios 기반 HTTP 클라이언트 유틸리티 (싱글톤 패턴)
 *
 * @description
 * - 전역 Axios 인스턴스를 싱글톤 패턴으로 관리
 * - 요청/응답 인터셉터를 통한 자동 인증 토큰 처리
 * - 중복 요청 방지 및 진행 중인 요청 관리
 * - HTTP 상태 코드별 에러 처리 및 로깅
 * - 네트워크 에러 및 타임아웃 처리
 * - 동적 설정 업데이트 및 인증 토큰 관리 기능 제공
 * - 모든 요청 취소 기능 및 개별 요청 취소 지원
 *
 * @remarks
 * - 환경변수를 통한 프록시 모드 및 API URL 설정 지원
 * - withCredentials 설정으로 쿠키 기반 인증 지원
 * - lodash를 사용한 깊은 병합으로 설정 업데이트 처리
 * - axios.Cancel을 통한 요청 취소 시 별도 에러 처리 없이 무시
 * - 30초 기본 타임아웃 설정으로 무한 대기 방지
 * - Content-Type은 기본적으로 application/json으로 설정
 *
 * @example
 * // 기본 사용법
 * const fetchData = fetchData();
 * const response = await fetchData.get('/users');
 *
 * // 토큰 설정
 * fetchData.setAuthToken('your-jwt-token');
 * const protectedData = await fetchData.get('/protected-route');
 *
 * // 토큰 제거
 * fetchData.clearAuthToken();
 *
 * // 현재 토큰 확인
 * fetchData.getAuthToken();
 *
 * // 모든 요청 취소
 * fetchData.cancelAllRequests();
 *
 * // 설정 업데이트
 * fetchData.updateConfig({
 *   timeout: 60000,
 *   headers: { 'Custom-Header': 'value' }
 * });
 *
 * @author 장지훈, 윤혜인
 * @created 2025-06-05
 */

/**********************************
 * Settings
 **********************************/
import axios from "axios";
import _ from "lodash";

/**********************************
 * Variables
 **********************************/
// Axios 인스턴스 저장
let instance = null;

// API 기본 URL
const BASE_URL = (() => {
  // 1순위: MSW가 활성화된 경우 location.origin에서 포트만 MSW 포트로 변경
  if (JSON.parse(import.meta.env.VITE_ENABLE_MSW || "false")) {
    const url = new URL(location.origin);
    url.port = import.meta.env.VITE_PORT_MSW;
    return url.toString();
  }

  // 2순위: 프록시 모드가 활성화된 경우 빈 문자열 반환
  if (JSON.parse(import.meta.env.VITE_ENABLE_PROXY || "false")) {
    return "";
  }

  // 3순위: 그 외의 경우 API URL 사용
  return `${import.meta.env.VITE_API_URL}`;
})();

// 인증 정보 전송 여부
const WITH_CREDENTIALS = JSON.parse(
  import.meta.env?.VITE_API_WITH_CREDENTIALS || "true",
);

// Axios 기본 설정
const DEFAULT_CONFIG = {
  baseURL: BASE_URL,
  timeout: 30000, // 30초
  withCredentials: WITH_CREDENTIALS,
  headers: {
    common: {
      "Content-Type": "application/json",
    },
  },
  credentials: "include",
  paramsSerializer: {
    indexes: true,
  },
};

/**********************************
 * Axios Configuration Module
 **********************************/
/**
 * 네트워크 요청 관리 및 Axios 설정 (싱글톤)
 *
 * @function fetchData
 *
 * @property {Map} pendingRequests 진행 중인 요청들을 관리하는 Map 객체
 * @property {number|null} loadingTimer 로딩 타이머 관리
 * @property {string|null} authToken 인증 토큰 저장
 * @property {Function} generateRequestKey 중복 요청 관리를 위한 유니크 키 생성 함수
 * @property {Function} cancelPendingRequest 진행 중인 요청 취소 함수
 * @property {Function} cancelAllRequests 모든 진행 중인 요청 취소 함수
 * @property {Function} setupRequestInterceptor 요청 인터셉터 설정 함수
 * @property {Function} handleResponseSuccess 응답 성공 처리 함수
 * @property {Function} handleResponseError 응답 에러 처리 함수
 * @property {Function} handleHttpErrorStatus HTTP 에러 상태 코드 처리 함수
 */

export function fetchData() {
  if (instance) return instance; // Axios 인스턴스 중복 생성 방지
  let pendingRequests = new Map(); // 진행 중인 요청 목록
  let loadingTimer = null; // 로딩 타이머
  let authToken = null; // 인증 토큰

  // 중복 요청 관리를 위한 유니크 키 생성 -----------------
  const generateRequestKey = (_config) => {
    const { method, url, params, data } = _config;
    return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;
  };

  // 진행 중인 요청 취소 -----------------
  const cancelPendingRequest = (_key) => {
    if (pendingRequests.has(_key)) {
      const controller = pendingRequests.get(_key);
      controller.abort();
      pendingRequests.delete(_key);
    }
  };

  // 모든 진행 중인 요청 취소 -----------------
  const cancelAllRequests = () => {
    pendingRequests.forEach((_controller) => {
      _controller.abort();
    });
    pendingRequests.clear();
  };

  // 요청 인터셉터 설정 -----------------
  const setupRequestInterceptor = (_instance) => {
    _instance.interceptors.request.use(
      async (_config) => {
        // [중복 요청 방지] & 로그인 요청이면 중복 요청 방지 생략
        if (_config.cancelToken !== false) {
          const _key = generateRequestKey(_config);
          // 동일한 요청이 진행 중이라면 취소
          cancelPendingRequest(_key);

          // 현재 요청에 대한 취소 컨트롤러 생성
          const controller = new AbortController();
          _config.signal = controller.signal;
          // 진행 중인 요청 목록에 추가
          pendingRequests.set(_key, controller);
        }

        // [인증 토큰 처리]
        if (authToken && !_config.skipAuth) {
          _config.headers.Authorization = `Bearer ${authToken}`;
        }

        // Firebase API 호출 시에는 인증 정보 전송 비활성화
        if (_config.url?.includes("firebase")) {
          _config.withCredentials = false;
        }

        return _config;
      },
      (_error) => {
        // 요청 전송 실패 시 에러 로깅 및 반환
        console.error("Request Error:", _error);
        return Promise.reject(_error);
      },
    );
  };

  // 응답 성공 처리 -----------------
  const handleResponseSuccess = (_response) => {
    // [요청 완료 처리]
    // 완료된 요청을 진행 중인 요청 목록에서 제거
    const key = generateRequestKey(_response.config);
    pendingRequests.delete(key);

    // [로딩 상태 관리]
    // 이전 로딩 타이머가 있다면 제거
    if (loadingTimer) {
      clearTimeout(loadingTimer);
    }

    // 500ms 후에 진행 중인 요청이 없으면 로딩 상태 해제
    loadingTimer = setTimeout(() => {
      if (pendingRequests.size === 0) {
        // store.dispatch('setLoading', false); // 스토어 연동 시 주석 해제
      }
    }, 500);

    return _response;
  };

  // HTTP 에러 상태 코드 처리 -----------------
  const handleHttpErrorStatus = (_status) => {
    switch (_status) {
      case 401:
        authToken = null;
        console.warn("인증이 만료되었습니다.");
        break;
      case 403:
        console.warn("권한이 없습니다.");
        break;
      case 500:
        console.error("서버 내부 오류");
        break;
      case 501:
        console.error("구현되지 않은 기능");
        break;
      case 502:
        console.error("잘못된 게이트웨이");
        break;
      case 503:
        console.error("서비스 사용 불가");
        break;
      case 504:
        console.error("게이트웨이 타임아웃");
        break;
    }
  };

  // 응답 에러 처리 -----------------
  const handleResponseError = async (_error) => {
    // [요청 취소 처리]
    // axios.Cancel로 취소된 요청은 별도 처리하지 않음
    if (axios.isCancel(_error)) {
      return new Promise(() => {});
    }

    // [진행 중인 요청 정리]
    // 에러가 발생한 요청을 목록에서 제거
    if (_error.config) {
      const key = generateRequestKey(_error.config);
      pendingRequests.delete(key);
    }

    // [네트워크 에러 처리]
    // 서버로부터 응답을 받지 못한 경우
    if (_error.request && !_error.response) {
      console.error("Network Error:", _error);
      return Promise.reject(_error);
    }

    // [HTTP 에러 처리]
    if (_error.response) {
      const status = _error.response.status;
      handleHttpErrorStatus(status);
    }

    return Promise.reject(_error);
  };

  // 전역 Axios 인스턴스 생성
  const axiosInstance = axios.create(DEFAULT_CONFIG);

  // 인스턴스 메서드 추가
  axiosInstance.setAuthToken = (_token) => {
    authToken = _token;
  };

  axiosInstance.clearAuthToken = () => {
    authToken = null;
  };

  axiosInstance.getAuthToken = () => {
    return authToken;
  };

  axiosInstance.cancelAllRequests = cancelAllRequests;

  // 설정 업데이트 메서드 추가
  axiosInstance.updateConfig = (_newConfig) => {
    const mergedConfig = _.mergeWith(
      {},
      axiosInstance.defaults,
      _newConfig,
      (objValue, srcValue) => {
        if (_.isArray(objValue)) {
          return srcValue; // 배열인 경우 덮어쓰기
        }
      },
    );

    Object.assign(axiosInstance.defaults, mergedConfig);
  };

  // 요청 및 응답 인터셉터 실행
  setupRequestInterceptor(axiosInstance);
  axiosInstance.interceptors.response.use(
    handleResponseSuccess,
    handleResponseError,
  );

  // 단일 인스턴스 저장
  instance = axiosInstance;

  return axiosInstance;
}
